import { TaskName } from '@/common/constants/taskNames.constants';
import { ServerMigration, TaskState } from '@schema';
import i18n from '@/common/i18n';
import { CANCELLABLE_MIGRATION_TYPE, ACTIVITY_TYPE_MAP } from '@/common/constants/servers.constants';
import type { TaskMap } from '@/common/store/modules/tasks/tasksNotificationMapper';

const MIGRATION_TYPE_MESSAGES: Record<ServerMigration['type'], {
  [TaskState.RUNNING]: (name: string) => string;
  [TaskState.SUCCESS]: (name: string) => string;
  [TaskState.FAILED]: (name: string) => string;
  [TaskState.CANCELLED]: (name: string) => string;
}> = {
  [ServerMigration.type.LIVE_MIGRATION]: {
    [TaskState.RUNNING]: (name) => i18n.t('Live migrating "{name}"...', { name }),
    [TaskState.SUCCESS]: (name) => i18n.t('Server "{name}" successfully live migrated', { name }),
    [TaskState.FAILED]: (name) => i18n.t('Failed to live migrate server "{name}"', { name }),
    [TaskState.CANCELLED]: (name) => i18n.t('Live migration of "{name}" cancelled', { name }),
  },
  [ServerMigration.type.MIGRATION]: {
    [TaskState.RUNNING]: (name) => i18n.t('Cold migrating "{name}"...', { name }),
    [TaskState.SUCCESS]: (name) => i18n.t('Server "{name}" successfully cold migrated', { name }),
    [TaskState.FAILED]: (name) => i18n.t('Failed to cold migrate server "{name}"', { name }),
    [TaskState.CANCELLED]: (name) => i18n.t('Cold migration of "{name}" cancelled', { name }),
  },
  [ServerMigration.type.EVACUATION]: {
    [TaskState.RUNNING]: (name) => i18n.t('Evacuating "{name}"...', { name }),
    [TaskState.SUCCESS]: (name) => i18n.t('Server "{name}" successfully evacuated', { name }),
    [TaskState.FAILED]: (name) => i18n.t('Failed to evacuate server "{name}"', { name }),
    [TaskState.CANCELLED]: (name) => i18n.t('Evacuation of "{name}" cancelled', { name }),
  },
  [ServerMigration.type.RESIZE]: {
    [TaskState.RUNNING]: (name) => i18n.t('Resizing "{name}"...', { name }),
    [TaskState.SUCCESS]: (name) => i18n.t('Server "{name}" successfully resized', { name }),
    [TaskState.FAILED]: (name) => i18n.t('Failed to resize server "{name}"', { name }),
    [TaskState.CANCELLED]: (name) => i18n.t('Resize of "{name}" cancelled', { name }),
  },
  [ServerMigration.type.LIVE_RESIZE]: {
    [TaskState.RUNNING]: (name) => i18n.t('Live resizing "{name}"...', { name }),
    [TaskState.SUCCESS]: (name) => i18n.t('Server "{name}" successfully live resized', { name }),
    [TaskState.FAILED]: (name) => i18n.t('Failed to live resize server "{name}"', { name }),
    [TaskState.CANCELLED]: (name) => i18n.t('Live resize of "{name}" cancelled', { name }),
  },
};

const getMigrationContext = (payload: ServerMigration) => {
  const serverName = payload.server_name;
  const migrationType = payload.type;
  const messages = MIGRATION_TYPE_MESSAGES[migrationType];
  const activityLabel = ACTIVITY_TYPE_MAP[migrationType];

  return { serverName, migrationType, messages, activityLabel };
};

const taskMap: TaskMap = {
  [TaskName.SERVER_MIGRATION]: {
    [TaskState.SUCCESS]: ({ payload }) => {
      const { serverName, messages, activityLabel } = getMigrationContext(payload);

      return {
        title: i18n.t('{type} completed', { type: activityLabel }),
        message: messages?.success(serverName)
      };
    },
    [TaskState.RUNNING]: ({ payload, progress }) => {
      const { serverName, messages } = getMigrationContext(payload);

      return {
        message: i18n.t('{0}% completed', [progress ?? 0]),
        title: messages.running(serverName),
        progress,
        actions: [
          {
            label: i18n.t('Cancel'),
            name: 'abortServerMigration',
            payload: { serverId: payload.server_id },
            display: payload.type === CANCELLABLE_MIGRATION_TYPE
          },
          {
            label: i18n.t('View in activities'),
            name: 'viewServerMigration',
            route: {
              name: 'activitiesDetails',
              params: {
                activityId: payload.migration_uuid as string,
              },
            }
          }
        ]
      };
    },
    [TaskState.CANCELLED]: ({ payload }) => {
      const { serverName, messages, activityLabel } = getMigrationContext(payload);

      return {
        title: i18n.t('{type} cancelled', { type: activityLabel }),
        message: messages?.cancelled(serverName),
      };
    },
    [TaskState.FAILED]: ({ payload, error }) => {
      const { activityLabel } = getMigrationContext(payload);

      return {
        title: i18n.t('{type} failed', { type: activityLabel }),
        message: error
      };
    }
  },
};

export default taskMap;
